import { MagnifyingGlassIcon } from '@phosphor-icons/react/dist/csr/MagnifyingGlass';
import { XCircleIcon } from '@phosphor-icons/react/dist/csr/XCircle';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../primitives/Button/Button';
import { ListState } from './ListState';

/**
 * No results — the list has rows, and the current query and filters hide all of
 * them.
 *
 * Handoff: "names the query and says how many filters are narrowing the list;
 * action is 'Clear filters' (secondary)." Both halves matter. An agent who has
 * forgotten a status filter from twenty minutes ago sees an empty list and
 * concludes the data is gone; "2 filters are narrowing this list" is the whole
 * fix, and it is a sentence, not a chip.
 *
 * The filter count is pluralised through the i18n layer rather than composed
 * here, because Arabic needs six forms for it and English needs two.
 */

export interface NoResultsCopy {
  title?: string;
  /** The search string, quoted back verbatim. */
  query?: string;
  /** How many filters are narrowing the list. Zero prints nothing. */
  filterCount?: number;
}

export function NoResultsState({
  title,
  query,
  filterCount,
  onClearFilters,
}: NoResultsCopy & { onClearFilters?: () => void }) {
  const { t } = useTranslation();

  return (
    <ListState
      icon={<MagnifyingGlassIcon size={26} weight="regular" />}
      title={title ?? t('list.noResultsTitle')}
      body={
        <>
          {query === undefined || query === '' ? null : <p>{t('list.noResultsBody', { query })}</p>}
          {filterCount === undefined || filterCount <= 0 ? null : (
            <p>{t('list.filterCount', { count: filterCount })}</p>
          )}
        </>
      }
      {...(onClearFilters === undefined
        ? {}
        : {
            action: (
              <Button
                variant="secondary"
                onClick={onClearFilters}
                icon={<XCircleIcon size={16} />}
              >
                {t('actions.clearFilters')}
              </Button>
            ),
          })}
    />
  );
}
